import { creatureAppearanceDocument } from '../authoring/creature-appearance';
import type { ItemFieldRow } from './item-field-reader';
import type { FromBaseRow } from './from-base-context';
import { constructEncounterCreatureSourceFromBase } from '../port/k1-compile/from-base/encounter-creature-source';
import { fromBaseClone, fromBaseRecord } from './from-base-context';
import { readItemField, readItemFieldReference } from './item-field-reader';
import { phpJsonString } from './php-json-values';
import { isPhpNumeric, phpJsonFloat, roundPhpDecimal } from './php-decimal-values';
import { withEncounterCreatureEnvironment } from './inventory-environment';

import playerColumns from './from-base-player-columns.json';
// The shared PHP rounding algorithm accepts arbitrary precision; its public
// union lists the older callers' places. Body persistence uses the same math at 3.
const roundBodyDecimal = roundPhpDecimal as (value: number, places: number) => number;

/** Native SELECT-row projection, before any PC rule window. Saved zero/null
 * columns are absent so later isset/default decisions match the native builder. */
export function constructPlayerSourceFromBase(source: FromBaseRow, value: (row: ItemFieldRow, key: string) => unknown): FromBaseRow {
    const row: FromBaseRow = {};
    for (const key of playerColumns) {
        const field = value(source, key);
        if (field !== null && field !== undefined && field !== '' && field !== 0 && field !== '0') row[key] = field;
    }
    return row;
}

/** Native source-only appearance decoding. Invalid stored documents are elided
 * by build_encounter_creature_arr rather than poisoning the whole PC compile. */
function appearanceSource(raw: unknown, body: boolean): FromBaseRow | null {
    try {
        if (!body) return creatureAppearanceDocument(raw);
        if (raw === null || raw === '') return null;
        if (typeof raw === 'string') {
            raw = JSON.parse(raw);
        }
        if (!fromBaseRecord(raw)) return null;
        const value = (row: ItemFieldRow, key: string) => readItemField(row, key);
        const schema = value(raw, 'schema');
        if (body) {
            const appearanceSchema = phpJsonString(value(raw, 'appearanceSchema')).trim(), controlContract = phpJsonString(value(raw, 'controlContract')).trim();
            const presentation = phpJsonString(value(raw, 'presentation')).trim(), presetRaw = value(raw, 'presetId');
            const preset = presetRaw == null || presetRaw === '' ? null : phpJsonString(presetRaw).trim();
            if (phpJsonString(schema).trim() !== 'ft.encounter-creature.body-appearance.v1' || appearanceSchema !== 'ft.appearance.humanoid.v2-candidate'
                || controlContract !== 'ft.humanoid.body-controls.v1' || !['feminine', 'masculine'].includes(presentation)
                || preset !== null && !['lean', 'average', 'athletic', 'powerful', 'soft_curvy', 'heavy', 'heavy_strong'].includes(preset)) return null;
            const rawValues = value(raw, 'values'); if (!fromBaseRecord(rawValues)) return null;
            const values: FromBaseRow = {};
            for (const [id, min, max] of [['ft.body.height', .9, 1.1], ['ft.body.size', -1, 1], ['ft.body.composition', -1, 1],
                ['ft.body.breast', -.75, 1], ['ft.body.abdomen', -.5, 1], ['ft.body.hips', -.75, .75]] as const) {
                const field = value(rawValues, id);
                if (!isPhpNumeric(field) || phpJsonFloat(field) < min || phpJsonFloat(field) > max || !Number.isFinite(phpJsonFloat(field))) return null;
                const rounded = roundBodyDecimal(phpJsonFloat(field), 3); values[id] = Math.abs(rounded) < .0005 ? 0 : rounded;
            }
            return { schema: 'ft.encounter-creature.body-appearance.v1', appearanceSchema, controlContract, presentation, presetId: preset, values };
        }
        return null;
    } catch { return null; }
}

export function constructPlayerEncounterSourceFromBase(source: FromBaseRow, encounter?: ItemFieldRow | null, summons?: readonly FromBaseRow[]): FromBaseRow {
    const ordinary = fromBaseClone(source); delete ordinary.body_appearance; delete ordinary.appearance;
    const row = constructEncounterCreatureSourceFromBase(withEncounterCreatureEnvironment(ordinary, encounter), summons);
    for (const key of ['body_appearance', 'appearance']) {
        const decoded = appearanceSource(key === 'appearance' ? readItemFieldReference(source, key) : readItemField(source, key), key === 'body_appearance');
        if (decoded) row[key] = decoded;
    }
    return row;
}
